import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import WelcomePage from "@/pages/WelcomePage";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import OverviewPage from "@/pages/dashboard/OverviewPage";
import NewBriefPage from "@/pages/dashboard/NewBriefPage";
import ProjectsPage from "@/pages/dashboard/ProjectsPage";
import ProjectDetailPage from "@/pages/dashboard/ProjectDetailPage";
import DeliveriesPage from "@/pages/dashboard/DeliveriesPage";
import CreditsPage from "@/pages/dashboard/CreditsPage";
import HelpPage from "@/pages/dashboard/HelpPage";
import SettingsPage from "@/pages/dashboard/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import { initLenis, initTidioScrollIsolation } from "@/lib/scroll";
import Starfield from "@/components/grapho/Starfield";
import Navbar from "@/components/grapho/Navbar";
import Hero from "@/components/grapho/Hero";
import TrustBar from "@/components/grapho/TrustBar";
import Problem from "@/components/grapho/Problem";
import HowItWorks from "@/components/grapho/HowItWorks";
import DesignRequestBox from "@/components/grapho/DesignRequestBox";
import CompetitorComparison from "@/components/grapho/CompetitorComparison";
import ExclusivitySection from "@/components/grapho/ExclusivitySection";
import SpeedSection from "@/components/grapho/SpeedSection";
import IncludesSection from "@/components/grapho/IncludesSection";
import Pricing from "@/components/grapho/Pricing";
import TrialSection from "@/components/grapho/TrialSection";
import Faq from "@/components/grapho/Faq";
import ContactSection from "@/components/grapho/ContactSection";
import TermsPage from "@/pages/TermsPage";
import Footer from "@/components/grapho/Footer";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err) {
    console.error(err);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[#070B14] p-6 text-center text-slate-200">
          <p>Something went wrong. Please refresh the page.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SignupSuccessBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(() => !!location.state?.signupSuccess);
  if (!visible) return null;
  return (
    <div
      data-testid="signup-success-banner"
      className="mx-auto mt-24 flex max-w-3xl items-center justify-between gap-4 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 px-5 py-4"
    >
      <p className="text-sm text-emerald-300">
        Your account is created. Purchase a plan to access your studio.
      </p>
      <button
        data-testid="signup-success-dismiss"
        onClick={() => {
          setVisible(false);
          navigate("/", { replace: true, state: {} });
        }}
        className="text-emerald-300/70 transition-colors hover:text-emerald-200"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

function Landing() {
  return (
    <>
      <Navbar />
      <main>
        <SignupSuccessBanner />
        <Hero />
        <TrustBar />
        <Problem />
        <HowItWorks />
        <DesignRequestBox />
        <CompetitorComparison />
        <ExclusivitySection />
        <SpeedSection />
        <IncludesSection />
        <Pricing />
        <TrialSection />
        <Faq />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
}

function App() {
  useEffect(() => {
    initLenis();
    initTidioScrollIsolation();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <div className="relative min-h-screen bg-[#070B14] text-slate-100 antialiased">
          <Starfield />
          <div className="grain" aria-hidden="true" />
          <div className="relative z-10">
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<OverviewPage />} />
                <Route path="new" element={<NewBriefPage />} />
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="projects/:id" element={<ProjectDetailPage />} />
                <Route path="deliveries" element={<DeliveriesPage />} />
                <Route path="credits" element={<CreditsPage />} />
                <Route path="help" element={<HelpPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/terms" element={<TermsPage />} />
            </Routes>
          </div>
          <Toaster theme="dark" position="top-center" />
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
